import React, { useState, useEffect } from 'react';
import { FlightInfo, FlightConnection } from './types';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { useToast } from '@/hooks/use-toast';
import { 
  Plane, 
  Trash, 
  ChevronDown, 
  ChevronRight,
  Clock,
  PlusCircle,
  ImagePlus,
  Search,
  Loader2,
  GripVertical,
  DollarSign,
  Edit,
  Check,
  CircleDot,
  Tag,
  Building,
  MapPin,
  CalendarClock,
  Calendar,
  Timer,
  Terminal,
  Image,
  FileText,
  Info,
  Bookmark,
  MoreHorizontal,
  Copy,
  ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  getDateFromISOString,
  getTimeFromISOString,
  calculateMinutesBetweenISOStrings,
  formatDuration,
  formatFlightNumber,
  validateFlightNumber,
  getAirlineName
} from '@/lib/quotation-utils';
import { isItemSavedInLibrary } from '@/lib/library-utils';
import { PlacesAutocompleteV1 } from '@/components/ui/places-autocomplete-v1';
import { Checkbox } from '@/components/ui/checkbox';
import { useAmadeusApi } from '@/hooks/useAmadeusApi';

// Import the utility functions for flight time calculations
import { calculateTotalFlightTime, calculateConnectionDuration } from './FlightManager';
import { SaveToLibraryModal } from './SaveToLibraryModal';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { PageSelectionModal } from './PageSelectionModal';
import { globalMoveFlightToPage as globalMoveFlightToPageFunc } from '@/hooks/useQuotationForm';

// Custom styled label with orange underline and icon
const StyledLabel = ({ 
  children, 
  htmlFor, 
  className, 
  icon: Icon = CircleDot,
  ...props 
}: React.ComponentProps<typeof Label> & { icon?: React.ElementType }) => (
  <Label 
    htmlFor={htmlFor}
    className={cn("border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left w-fit text-sm font-medium text-gray-700", className)}
    {...props}
  >
    <Icon className="h-3.5 w-3.5 text-voah-coral flex-shrink-0" />
    <span className="whitespace-nowrap">{children}</span>
  </Label>
);

// Update input styles by creating a component wrapper
const FormInput = ({ className, ...props }: React.ComponentProps<typeof Input>) => (
  <Input
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

const FormTextarea = ({ className, ...props }: React.ComponentProps<typeof Textarea>) => (
  <Textarea
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

interface FlightFormProps {
  flight: FlightInfo;
  index: number;
  pageId: string;
  updateFlight: (pageId: string, flightId: string, field: keyof FlightInfo, value: any) => void;
  batchUpdateFlight?: (flightId: string, updates: Partial<FlightInfo>) => void;
  updateFlightConnection?: (pageId: string, flightId: string, connectionId: string, field: string, value: any) => void;
  addFlightConnection?: (pageId: string, flightId: string) => void;
  removeFlightConnection?: (pageId: string, flightId: string, connectionId: string) => void;
  deleteFlight: (pageId: string, flightId: string) => void;
  duplicateFlight?: (pageId: string, flightId: string) => void;
  moveFlightToPage?: (sourcePageId: string, flightId: string, targetPageId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  searchFlight?: (flightId: string) => Promise<void>;
  searchConnectionFlight?: (flightId: string, connectionId: string) => Promise<void>;
  allPages?: any[]; // All pages in the quotation
}

export function FlightForm({
  flight,
  index,
  pageId,
  updateFlight,
  batchUpdateFlight,
  updateFlightConnection,
  addFlightConnection,
  removeFlightConnection,
  deleteFlight,
  duplicateFlight,
  moveFlightToPage,
  isCollapsed,
  onToggleCollapse,
  searchFlight,
  searchConnectionFlight,
  allPages
}: FlightFormProps) {
  const { toast } = useToast();
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);
  const [isSearchingFlight, setIsSearchingFlight] = useState(false);
  const [isSearchingConnection, setIsSearchingConnection] = useState(false);
  const [currentConnectionIndex, setCurrentConnectionIndex] = useState<number | null>(null);
  const [shouldShowDeleteConfirm, setShouldShowDeleteConfirm] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSaveToLibraryOpen, setIsSaveToLibraryOpen] = useState(false);
  const [isSavedToLibrary, setIsSavedToLibrary] = useState(false);
  const [flightToMove, setFlightToMove] = useState<string | null>(null);
  const [isPageSelectionOpen, setIsPageSelectionOpen] = useState(false);

  // Verificar se o item já está salvo na biblioteca
  useEffect(() => {
    let isMounted = true;
    
    const checkLibraryStatus = async () => {
      try {
        const isSaved = await isItemSavedInLibrary(flight, 'flight');
        if (isMounted) {
          setIsSavedToLibrary(isSaved);
        }
      } catch (error) {
        console.error('Erro ao verificar status na biblioteca:', error);
        if (isMounted) {
          setIsSavedToLibrary(false);
        }
      }
    };
    
    checkLibraryStatus();
    
    return () => {
      isMounted = false; // cleanup to prevent setting state on unmounted component
    };
  }, [flight]);

  // Helper function to create a flight title
  const createFlightTitle = (flight: FlightInfo): string => {
    if (flight.airline && flight.flightNumber) {
      return `${flight.airline} - ${flight.flightNumber}`;
    } else if (flight.departureAirport && flight.arrivalAirport) {
      return `${flight.departureAirport} → ${flight.arrivalAirport}`;
    }
    return '';
  };

  const [flightTitle, setFlightTitle] = useState<string>(() => {
    // Initialize title based on current flight
    const title = createFlightTitle(flight);
    return title || `Voo #${index + 1}`;
  });

  // Effect to keep flightTitle in sync with flight details
  useEffect(() => {
    const title = createFlightTitle(flight);
    setFlightTitle(title || `Voo #${index + 1}`);
  }, [flight.airline, flight.flightNumber, flight.departureAirport, flight.arrivalAirport, index]);

  // Function to finish title editing
  const handleFinishTitleEdit = () => {
    setIsEditingTitle(false);
    
    // If title is different from original, try to extract airline and flight number
    const parts = flightTitle.split('-').map(part => part.trim());
    if (parts.length === 2) {
      handleUpdate('airline', parts[0]);
      handleUpdate('flightNumber', parts[1]);
    }
  };

  // Format number with thousands separator
  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  // Handle updates to a field
  const handleUpdate = (field: keyof FlightInfo, value: any) => {
    updateFlight(pageId, flight.id, field, value);
  };

  // Handle updates to a connection field
  const handleConnectionUpdate = (connectionId: string, field: string, value: any) => {
    if (updateFlightConnection) {
      updateFlightConnection(pageId, flight.id, connectionId, field, value);
    }
  };

  // Add a new connection
  const handleAddConnection = () => {
    if (addFlightConnection) {
      addFlightConnection(pageId, flight.id);
    }
  };

  // Remove a connection
  const handleRemoveConnection = (connectionId: string) => {
    if (removeFlightConnection) {
      removeFlightConnection(pageId, flight.id, connectionId);
    }
  };

  // Search for flight information
  const handleSearchFlight = async () => {
    if (!flight.flightNumber) {
      toast({
        title: "Número do voo necessário",
        description: "Por favor, informe o número do voo para realizar a busca.",
        variant: "destructive",
      });
      return;
    }

    if (!validateFlightNumber(flight.flightNumber)) {
      toast({
        title: "Formato incorreto",
        description: "O número do voo deve conter 2 letras seguidas de 1 a 4 números (ex: LA1234, la 1234).",
        variant: "destructive",
      });
      return;
    }
    
    if (!flight.departureDateTime) {
      toast({
        title: "Data necessária",
        description: "Por favor, selecione a data do voo para realizar a busca.",
        variant: "destructive",
      });
      return;
    }

    setIsSearchingFlight(true);

    try {
      if (searchFlight) {
        await searchFlight(flight.id);
      }
    } catch (error) {
      console.error("Erro ao buscar informações do voo:", error);
      toast({
        title: "Erro na busca",
        description: "Ocorreu um erro ao buscar as informações do voo. Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsSearchingFlight(false);
    }
  };

  // Search for connection flight information
  const handleSearchConnection = async (connectionId: string, connectionIndex: number) => {
    const connection = flight.connections.find(c => c.id === connectionId);
    if (!connection) return;

    if (!connection.flightNumber) {
      toast({
        title: "Número do voo necessário",
        description: "Por favor, informe o número do voo para realizar a busca.",
        variant: "destructive",
      });
      return;
    }

    if (!validateFlightNumber(connection.flightNumber)) {
      toast({
        title: "Formato incorreto",
        description: "O número do voo deve conter 2 letras seguidas de 1 a 4 números (ex: LA1234, la 1234).",
        variant: "destructive",
      });
      return;
    }
    
    if (!connection.departureDateTime) {
      toast({
        title: "Data necessária",
        description: "Por favor, selecione a data do voo para realizar a busca.",
        variant: "destructive",
      });
      return;
    }

    setIsSearchingConnection(true);
    setCurrentConnectionIndex(connectionIndex);

    try {
      if (searchConnectionFlight) {
        await searchConnectionFlight(flight.id, connectionId);
      }
    } catch (error) {
      console.error("Erro ao buscar informações do voo de conexão:", error);
      toast({
        title: "Erro na busca",
        description: "Ocorreu um erro ao buscar as informações do voo de conexão. Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsSearchingConnection(false);
      setCurrentConnectionIndex(null);
    }
  };

  // Render function for flight number field with search button
  const renderFlightNumberField = () => {
    return (
      <div className="flex items-center gap-2">
        <FormInput 
          id={`flight-number-${flight.id}`}
          value={flight.flightNumber || ''} 
          onChange={(e) => handleUpdate('flightNumber', e.target.value)}
          placeholder="Ex: LA1234" 
          className="max-w-[120px]"
        />
        <Button 
          onClick={handleSearchFlight} 
          size="sm" 
          variant="outline"
          disabled={isSearchingFlight}
        >
          {isSearchingFlight ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </Button>
      </div>
    );
  };

  // Render function for connection's flight number field with search button
  const renderConnectionFlightNumberField = (connection: FlightConnection, connectionIdx: number) => {
    return (
      <div className="flex items-center gap-2">
        <FormInput 
          id={`flight-number-${connection.id}`}
          value={connection.flightNumber || ''} 
          onChange={(e) => handleConnectionUpdate(connection.id, 'flightNumber', e.target.value)}
          placeholder="Ex: LA1234" 
          className="max-w-[120px]"
        />
        <Button 
          onClick={() => handleSearchConnection(connection.id, connectionIdx)} 
          size="sm" 
          variant="outline"
          disabled={isSearchingConnection && currentConnectionIndex === connectionIdx}
        >
          {isSearchingConnection && currentConnectionIndex === connectionIdx ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </Button>
      </div>
    );
  };

  // Render connection form fields
  const renderConnectionFields = (connection: FlightConnection, connectionIdx: number) => {
    return (
      <Card key={connection.id} className="border border-gray-200 mb-4">
        <CardHeader className="p-3 bg-gray-50 flex flex-row items-center border-b">
          <div className="flex items-center space-x-2">
            <Plane className="h-4 w-4 text-blue-500" />
            <h4 className="text-sm font-medium">Conexão #{connectionIdx + 1}</h4>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="p-0 h-7 w-7 ml-auto"
            onClick={() => handleRemoveConnection(connection.id)}
          >
            <Trash className="h-4 w-4 text-gray-500 hover:text-red-500" />
          </Button>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cia Aérea</Label>
              <Input
                value={connection.airline || ''}
                onChange={(e) => handleConnectionUpdate(connection.id, 'airline', e.target.value)}
                placeholder="Ex: LA (LATAM)"
              />
            </div>
            <div className="space-y-2">
              <Label>Número do Voo</Label>
              <div className="flex gap-2">
                <Input
                  value={connection.flightNumber || ''}
                  onChange={(e) => handleConnectionUpdate(connection.id, 'flightNumber', e.target.value)}
                  placeholder="Ex: LA3517"
                  className="flex-grow"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSearchConnection(connection.id, connectionIdx)}
                  disabled={isSearchingConnection && currentConnectionIndex === connectionIdx}
                >
                  {isSearchingConnection && currentConnectionIndex === connectionIdx ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Aeroporto de Partida</Label>
              <Input
                value={connection.departureAirport || ''}
                onChange={(e) => handleConnectionUpdate(connection.id, 'departureAirport', e.target.value)}
                placeholder="Ex: GRU"
              />
            </div>
            <div className="space-y-2">
              <Label>Aeroporto de Chegada</Label>
              <Input
                value={connection.arrivalAirport || ''}
                onChange={(e) => handleConnectionUpdate(connection.id, 'arrivalAirport', e.target.value)}
                placeholder="Ex: SDU"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data e Hora de Partida</Label>
              <Input
                type="datetime-local"
                value={connection.departureDateTime || ''}
                onChange={(e) => handleConnectionUpdate(connection.id, 'departureDateTime', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Data e Hora de Chegada</Label>
              <Input
                type="datetime-local"
                value={connection.arrivalDateTime || ''}
                onChange={(e) => handleConnectionUpdate(connection.id, 'arrivalDateTime', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Duração do Voo</Label>
              <Input
                value={connection.flightDuration || ''}
                onChange={(e) => handleConnectionUpdate(connection.id, 'flightDuration', e.target.value)}
                placeholder="Ex: 2h 15m"
              />
            </div>
            <div className="space-y-2">
              <Label>Tempo de Espera</Label>
              <Input
                value={connection.waitingTime || calculateConnectionDuration(flight, connection, connectionIdx)}
                onChange={(e) => handleConnectionUpdate(connection.id, 'waitingTime', e.target.value)}
                placeholder="Ex: 1h 30m"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea
              value={connection.notes || ''}
              onChange={(e) => handleConnectionUpdate(connection.id, 'notes', e.target.value)}
              placeholder="Informações adicionais sobre esta conexão..."
              rows={2}
            />
          </div>
        </CardContent>
      </Card>
    );
  };

  // Handler para o botão de salvar na biblioteca
  const handleSaveToLibrary = (e: React.MouseEvent) => {
    e.stopPropagation(); // Impedir que o clique colapse/expanda o item
    setIsSaveToLibraryOpen(true);
  };

  // Função para iniciar movimento de voo
  const handleInitiateFlightMove = (flightId: string) => {
    console.log('[FlightForm] handleInitiateFlightMove called:', {
      flightId,
      hasMoveFunction: typeof moveFlightToPage === 'function',
      hasGlobalMoveFunction: typeof globalMoveFlightToPageFunc === 'function',
      allPagesAvailable: Array.isArray(allPages),
      allPagesCount: allPages?.length,
      currentPageId: pageId
    });
    
    // Verificar disponibilidade da função (local ou global)
    if (!moveFlightToPage && !globalMoveFlightToPageFunc) {
      console.log('[FlightForm] No movement function available (local or global)');
      toast({
        title: "Operação não disponível",
        description: "A função para mover voos não está disponível.",
      });
      return;
    }
    
    // Garantir que allPages é um array e possui mais de uma página
    const availablePages = Array.isArray(allPages) && allPages.length > 1
      ? allPages.filter(p => p.id !== pageId)
      : [];
    
    // Verificar se há páginas disponíveis
    if (availablePages.length === 0) {
      console.log('[FlightForm] No available pages after filtering');
      toast({
        title: "Operação não disponível",
        description: "É necessário ter mais de uma página disponível para mover o voo.",
      });
      return;
    }
    
    // Configurar estado e abrir modal
    setFlightToMove(flightId);
    setIsPageSelectionOpen(true);
  };

  // Função para concluir movimento de voo
  const handleMoveToPage = (targetPageId: string) => {
    console.log("[FlightForm] handleMoveToPage called with targetPageId:", targetPageId);
    
    // Verificar se há voo para mover
    if (!flightToMove) {
      console.log("[FlightForm] No flight to move, aborting");
      return;
    }
    
    // Fechar o modal imediatamente
    setIsPageSelectionOpen(false);
    
    // Log detalhado sobre disponibilidade de funções
    console.log("[FlightForm] Movement functions availability:", { 
      moveFlightToPage: {
        exists: !!moveFlightToPage,
        type: typeof moveFlightToPage,
        isFunction: typeof moveFlightToPage === 'function'
      },
      globalMoveFlightToPageFunc: {
        exists: !!globalMoveFlightToPageFunc,
        type: typeof globalMoveFlightToPageFunc,
        isFunction: typeof globalMoveFlightToPageFunc === 'function'
      },
      hasAllRequiredParams: pageId && flightToMove && targetPageId
    });
    
    try {
      // Abordagem primária: usar função local se disponível
      if (typeof moveFlightToPage === 'function') {
        console.log("[FlightForm] Calling local function with:", {
          sourcePageId: pageId,
          flightId: flightToMove,
          targetPageId
        });
        
        moveFlightToPage(pageId, flightToMove, targetPageId);
        toast({
          title: "Voo movido",
          description: "O voo foi movido para a página selecionada.",
        });
      } 
      // Abordagem secundária: usar função global como fallback
      else if (typeof globalMoveFlightToPageFunc === 'function') {
        console.log("[FlightForm] Calling global function with:", {
          sourcePageId: pageId,
          flightId: flightToMove,
          targetPageId
        });
        
        globalMoveFlightToPageFunc(pageId, flightToMove, targetPageId);
        toast({
          title: "Voo movido",
          description: "O voo foi movido para a página selecionada.",
        });
      } 
      // Fallback: mostrar mensagem de indisponibilidade
      else {
        console.log("[FlightForm] No movement function available:", {
          propFunction: typeof moveFlightToPage,
          globalFunction: typeof globalMoveFlightToPageFunc
        });
        
        toast({
          title: "Funcionalidade não disponível",
          description: "A função de mover não está disponível.",
        });
      }
    } catch (error) {
      console.error("[FlightForm] Error moving flight:", error);
      toast({
        title: "Erro ao mover voo",
        description: "Ocorreu um erro ao tentar mover o voo: " + (error instanceof Error ? error.message : String(error)),
        variant: "destructive"
      });
    }
    
    // Sempre limpar o estado
    setFlightToMove(null);
  };

  // Dropdown content with "Move" option
  const renderDropdownContent = () => (
    <DropdownMenuContent align="end" className="w-[180px]">
      {duplicateFlight && (
        <DropdownMenuItem 
          onClick={(e) => {
            e.stopPropagation();
            duplicateFlight(pageId, flight.id);
          }}
        >
          <Copy className="h-4 w-4 mr-2" />
          <span>Duplicar</span>
        </DropdownMenuItem>
      )}
      
      {(moveFlightToPage || typeof globalMoveFlightToPageFunc === 'function') && (
        <DropdownMenuItem 
          onClick={(e) => {
            e.stopPropagation();
            handleInitiateFlightMove(flight.id);
          }}
        >
          <ArrowRight className="h-4 w-4 mr-2" />
          <span>Mover</span>
        </DropdownMenuItem>
      )}
      
      <DropdownMenuItem 
        onClick={(e) => {
          e.stopPropagation();
          deleteFlight(pageId, flight.id);
        }}
        className="text-red-600"
      >
        <Trash className="h-4 w-4 mr-2" />
        <span>Excluir</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  );

  return (
    <>
      <Card className={cn(
        "border shadow-sm transition-all border-l-4 border-l-blue-500",
        isCollapsed && "border-transparent border-l-blue-500"
      )}>
        <CardHeader 
          className={cn(
            "cursor-pointer flex items-center justify-between",
            isCollapsed ? "py-2 px-3" : "py-3 px-4"
          )}
          onClick={onToggleCollapse}
        >
          <div className="flex items-center gap-2 w-full">
            <Plane className="h-4 w-4 text-blue-500" />
            
            {isEditingTitle ? (
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={flightTitle}
                  onChange={(e) => setFlightTitle(e.target.value)}
                  className="h-7 mr-2 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-transparent"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleFinishTitleEdit();
                    }
                  }}
                  onBlur={handleFinishTitleEdit}
                />
              </div>
            ) : (
              <>
                {isCollapsed ? (
                  <span className="font-medium">{flightTitle}</span>
                ) : (
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingTitle(true);
                    }}
                  >
                    <h3 className="font-medium">{flightTitle}</h3>
                  </div>
                )}
              </>
            )}
            
            {/* Quick info for collapsed view */}
            {isCollapsed && (
              <>
                {flight.price !== null && flight.price !== undefined && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <DollarSign className="h-3.5 w-3.5 mr-1" />
                    <span>{formatNumber(flight.price)} {flight.currency || 'BRL'}</span>
                  </div>
                )}
                
                {flight.connections && flight.connections.length >= 1 && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <PlusCircle className="h-3.5 w-3.5 mr-1" />
                    <span>{flight.connections.length} conexões</span>
                  </div>
                )}

                <div className="flex items-center ml-3 text-sm text-gray-500">
                  <Clock className="h-3.5 w-3.5 mr-1" />
                  <span>{calculateTotalFlightTime(flight)}</span>
                </div>
              </>
            )}
            
            <div className="flex items-center ml-auto">
              {/* Botão de Salvar na Biblioteca (bookmark) */}
              <Button
                variant={isSavedToLibrary ? "default" : "ghost"}
                size="icon"
                className={cn(
                  "h-8 w-8 transition-colors",
                  isSavedToLibrary 
                    ? "bg-amber-500 hover:bg-amber-600" 
                    : "hover:text-amber-500"
                )}
                onClick={handleSaveToLibrary}
                title={isSavedToLibrary ? "Item salvo na biblioteca" : "Salvar na Biblioteca"}
              >
                <Bookmark className={cn(
                  "h-4 w-4", 
                  isSavedToLibrary 
                    ? "text-white" 
                    : "text-gray-500 hover:text-amber-500"
                )} />
              </Button>
              
              {/* Dropdown menu with actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4 text-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                {renderDropdownContent()}
              </DropdownMenu>
              
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </div>
          </div>
        </CardHeader>
        
        {!isCollapsed && (
          <CardContent className="p-4 pt-0 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center gap-4">
                <StyledLabel htmlFor={`flight-number-${flight.id}`} className="w-32" icon={Plane}>
                  Número do Voo
                  <span className="text-red-500 ml-1">*</span>
                </StyledLabel>
                {renderFlightNumberField()}
              </div>
              <div className="flex items-center gap-4">
                <StyledLabel htmlFor={`airline-${flight.id}`} className="w-32" icon={Plane}>Cia Aérea</StyledLabel>
                <FormInput 
                      id={`airline-${flight.id}`}
                      value={flight.airline || ''} 
                      onChange={(e) => handleUpdate('airline', e.target.value)}
                      placeholder="Ex: LATAM Airlines" 
                  className="w-full"
                    />
                  </div>
                  </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Departure Container - Blue */}
              <div className="border-2 border-blue-400 p-4 rounded-md bg-blue-50">
                <h3 className="text-sm font-medium mb-3 text-blue-800">Informações de Partida</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <StyledLabel className="w-32" icon={Building}>Aeroporto</StyledLabel>
                    <FormInput 
                      id={`departure-airport-${flight.id}`}
                      value={flight.departureAirport || ''} 
                      onChange={(e) => handleUpdate('departureAirport', e.target.value)}
                      placeholder="Ex: GRU" 
                      className="max-w-[120px]"
                    />
                  </div>

                  <div className="flex items-center gap-4">
                    <StyledLabel className="w-32" icon={Calendar}>Data<span className="text-red-500 ml-1">*</span></StyledLabel>
                    <FormInput 
                      type="date"
                      value={flight.departureDateTime ? flight.departureDateTime.split('T')[0] : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          const currentTime = flight.departureDateTime 
                            ? flight.departureDateTime.split('T')[1].substring(0, 5)
                            : "00:00";
                            
                          const newDateTime = `${e.target.value}T${currentTime}:00`;
                          handleUpdate('departureDateTime', newDateTime);
                        }
                      }}
                      className="max-w-[160px]"
                    />
                  </div>

                  <div className="flex items-center gap-4">
                    <StyledLabel className="w-32" icon={Clock}>Hora</StyledLabel>
                    <FormInput 
                      type="time"
                      value={flight.departureDateTime ? flight.departureDateTime.split('T')[1].substring(0, 5) : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          const currentDate = flight.departureDateTime 
                            ? flight.departureDateTime.split('T')[0]
                            : new Date().toISOString().split('T')[0];
                            
                          const newDateTime = `${currentDate}T${e.target.value}:00`;
                          handleUpdate('departureDateTime', newDateTime);
                        }
                      }}
                      className="max-w-[120px]"
                    />
                  </div>

                  <div className="flex items-center gap-4">
                    <StyledLabel className="w-32" icon={Terminal}>Terminal</StyledLabel>
                    <FormInput 
                      id={`departure-terminal-${flight.id}`}
                      value={flight.departureTerminal || ''} 
                      onChange={(e) => handleUpdate('departureTerminal', e.target.value)}
                      placeholder="Ex: 2" 
                      className="max-w-[80px]"
                    />
                  </div>
                </div>
              </div>

              {/* Arrival Container - Green */}
              <div className="border-2 border-green-400 p-4 rounded-md bg-green-50">
                <h3 className="text-sm font-medium mb-3 text-green-800">Informações de Chegada</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <StyledLabel className="w-32" icon={Building}>Aeroporto</StyledLabel>
                    <FormInput 
                      id={`arrival-airport-${flight.id}`}
                      value={flight.arrivalAirport || ''} 
                      onChange={(e) => handleUpdate('arrivalAirport', e.target.value)}
                      placeholder="Ex: LAX" 
                      className="max-w-[120px]"
                    />
                  </div>

                  <div className="flex items-center gap-4">
                    <StyledLabel className="w-32" icon={Calendar}>Data</StyledLabel>
                    <FormInput 
                      type="date"
                      value={flight.arrivalDateTime ? flight.arrivalDateTime.split('T')[0] : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          const currentTime = flight.arrivalDateTime 
                            ? flight.arrivalDateTime.split('T')[1].substring(0, 5)
                            : "00:00";
                            
                          const newDateTime = `${e.target.value}T${currentTime}:00`;
                          handleUpdate('arrivalDateTime', newDateTime);
                        }
                      }}
                      className="max-w-[160px]"
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <StyledLabel className="w-32" icon={Clock}>Hora</StyledLabel>
                    <FormInput 
                      type="time"
                      value={flight.arrivalDateTime ? flight.arrivalDateTime.split('T')[1].substring(0, 5) : ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          const currentDate = flight.arrivalDateTime 
                            ? flight.arrivalDateTime.split('T')[0]
                            : new Date().toISOString().split('T')[0];
                            
                          const newDateTime = `${currentDate}T${e.target.value}:00`;
                          handleUpdate('arrivalDateTime', newDateTime);
                        }
                      }}
                      className="max-w-[120px]"
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <StyledLabel className="w-32" icon={Terminal}>Terminal</StyledLabel>
                    <FormInput 
                      id={`arrival-terminal-${flight.id}`}
                      value={flight.arrivalTerminal || ''} 
                      onChange={(e) => handleUpdate('arrivalTerminal', e.target.value)}
                      placeholder="Ex: 3" 
                      className="max-w-[80px]"
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <StyledLabel className="w-32" icon={Timer}>Duração</StyledLabel>
                    <FormInput 
                      value={flight.flightDuration || ''}
                      onChange={(e) => handleUpdate('flightDuration', e.target.value)}
                      placeholder="Ex: 2h 30m"
                      className="max-w-[120px]"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Add Connection Button */}
            <div className="mt-4">
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={handleAddConnection}
                className="w-full bg-purple-50 border-purple-200 hover:bg-purple-100 text-purple-800"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                <span>Adicionar Conexão</span>
              </Button>
            </div>

            {/* Connections Section */}
            {flight.connections && flight.connections.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold mb-3">Conexões</h4>
                <div className="space-y-4">
                  {flight.connections.map((connection, connectionIdx) => (
                    <div key={connection.id} className="border-2 border-purple-300 rounded-md p-4 bg-purple-50">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-medium">{`Conexão ${connectionIdx + 1}`}</h5>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-gray-500 hover:text-red-500 hover:bg-red-50"
                          title="Remover Conexão"
                          onClick={() => handleRemoveConnection(connection.id)}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Waiting time display */}
                      <div className="mb-3 flex items-center justify-center text-xs text-gray-500 bg-gray-100 py-2 rounded-md">
                        <Clock className="h-3 w-3 mr-1" />
                        <span>
                          Tempo de espera: {calculateConnectionDuration(flight, connection, connectionIdx)}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-4">
                          <StyledLabel htmlFor={`flight-number-${connection.id}`} className="w-32" icon={Plane}>
                            Número do Voo
                            <span className="text-red-500 ml-1">*</span>
                          </StyledLabel>
                          {renderConnectionFlightNumberField(connection, connectionIdx)}
                        </div>
                        <div className="flex items-center gap-4">
                          <StyledLabel htmlFor={`airline-${connection.id}`} className="w-32" icon={Plane}>Companhia Aérea</StyledLabel>
                          <FormInput 
                            id={`airline-${connection.id}`}
                            value={connection.airline || ''} 
                            onChange={(e) => handleConnectionUpdate(connection.id, 'airline', e.target.value)}
                            placeholder="Ex: LATAM Airlines" 
                            className="w-full"
                          />
                        </div>
                      </div>

                      {/* Color-coded departure/arrival sections for connections */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        {/* Departure Container */}
                        <div className="border-2 border-blue-400 p-4 rounded-md bg-blue-50">
                          <h3 className="text-sm font-medium mb-3 text-blue-800">Informações de Partida</h3>
                          <div className="space-y-4">
                            <div className="flex items-center gap-4">
                              <StyledLabel className="w-32" icon={Building}>Aeroporto</StyledLabel>
                              <FormInput 
                                id={`departure-airport-${connection.id}`}
                                value={connection.departureAirport || ''} 
                                onChange={(e) => handleConnectionUpdate(connection.id, 'departureAirport', e.target.value)}
                                placeholder="Ex: GRU" 
                                className="max-w-[120px]"
                              />
                            </div>

                            <div className="flex items-center gap-4">
                              <StyledLabel className="w-32" icon={Calendar}>Data<span className="text-red-500 ml-1">*</span></StyledLabel>
                              <FormInput 
                                type="date"
                                value={connection.departureDateTime ? connection.departureDateTime.split('T')[0] : ''}
                                onChange={(e) => {
                                  if (e.target.value) {
                                    const currentTime = connection.departureDateTime 
                                      ? connection.departureDateTime.split('T')[1].substring(0, 5)
                                      : "00:00";
                                      
                                    const newDateTime = `${e.target.value}T${currentTime}:00`;
                                    handleConnectionUpdate(connection.id, 'departureDateTime', newDateTime);
                                  }
                                }}
                                className="max-w-[160px]"
                              />
                            </div>

                            <div className="flex items-center gap-4">
                              <StyledLabel className="w-32" icon={Clock}>Hora</StyledLabel>
                              <FormInput 
                                type="time"
                                value={connection.departureDateTime ? connection.departureDateTime.split('T')[1].substring(0, 5) : ''}
                                onChange={(e) => {
                                  if (e.target.value) {
                                    const currentDate = connection.departureDateTime 
                                      ? connection.departureDateTime.split('T')[0]
                                      : new Date().toISOString().split('T')[0];
                                      
                                    const newDateTime = `${currentDate}T${e.target.value}:00`;
                                    handleConnectionUpdate(connection.id, 'departureDateTime', newDateTime);
                                  }
                                }}
                                className="max-w-[120px]"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Arrival Container */}
                        <div className="border-2 border-green-400 p-4 rounded-md bg-green-50">
                          <h3 className="text-sm font-medium mb-3 text-green-800">Informações de Chegada</h3>
                          <div className="space-y-4">
                            <div className="flex items-center gap-4">
                              <StyledLabel className="w-32" icon={Building}>Aeroporto</StyledLabel>
                              <FormInput 
                                id={`arrival-airport-${connection.id}`}
                                value={connection.arrivalAirport || ''} 
                                onChange={(e) => handleConnectionUpdate(connection.id, 'arrivalAirport', e.target.value)}
                                placeholder="Ex: LAX" 
                                className="max-w-[120px]"
                              />
                            </div>

                            <div className="flex items-center gap-4">
                              <StyledLabel className="w-32" icon={Calendar}>Data</StyledLabel>
                              <FormInput 
                                type="date"
                                value={connection.arrivalDateTime ? connection.arrivalDateTime.split('T')[0] : ''}
                                onChange={(e) => {
                                  if (e.target.value) {
                                    const currentTime = connection.arrivalDateTime 
                                      ? connection.arrivalDateTime.split('T')[1].substring(0, 5)
                                      : "00:00";
                                      
                                    const newDateTime = `${e.target.value}T${currentTime}:00`;
                                    handleConnectionUpdate(connection.id, 'arrivalDateTime', newDateTime);
                                  }
                                }}
                                className="max-w-[160px]"
                              />
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <StyledLabel className="w-32" icon={Clock}>Hora</StyledLabel>
                              <FormInput 
                                type="time"
                                value={connection.arrivalDateTime ? connection.arrivalDateTime.split('T')[1].substring(0, 5) : ''}
                                onChange={(e) => {
                                  if (e.target.value) {
                                    const currentDate = connection.arrivalDateTime 
                                      ? connection.arrivalDateTime.split('T')[0]
                                      : new Date().toISOString().split('T')[0];
                                      
                                    const newDateTime = `${currentDate}T${e.target.value}:00`;
                                    handleConnectionUpdate(connection.id, 'arrivalDateTime', newDateTime);
                                  }
                                }}
                                className="max-w-[120px]"
                              />
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <StyledLabel className="w-32" icon={Timer}>Duração</StyledLabel>
                              <FormInput 
                                value={connection.flightDuration || ''}
                                onChange={(e) => handleConnectionUpdate(connection.id, 'flightDuration', e.target.value)}
                                placeholder="Ex: 2h 15m"
                                className="max-w-[120px]"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-100 py-2 px-4 rounded-md justify-center">
                <Clock className="h-4 w-4 mr-1" />
                <span>Tempo total de voo (incluindo conexões): <strong>{calculateTotalFlightTime(flight)}</strong></span>
              </div>
            </div>
            
            <div className="pt-4 border-t mt-4">
              <div className="flex justify-between items-center">
                <StyledLabel icon={Image}>Imagens</StyledLabel>
                <div className="flex items-center gap-2">
                  {flight.images && flight.images.length > 0 ? (
                    <div className="flex items-center gap-1">
                      <div 
                        className="h-8 w-8 rounded-md overflow-hidden border border-gray-200 shadow-sm hover:border-[#087a83] cursor-pointer transition-all hover:scale-105"
                        onClick={() => setIsImageManagerOpen(true)}
                      >
                        <img 
                          src={flight.images[0]} 
                          alt="Miniatura" 
                          className="h-full w-full object-cover"
                        />
                      </div>
                      {flight.images.length > 1 && (
                        <span 
                          className="text-xs text-gray-500 cursor-pointer hover:text-[#087a83]"
                          onClick={() => setIsImageManagerOpen(true)}
                        >
                          +{flight.images.length - 1}
                        </span>
                      )}
                    </div>
                  ) : (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => setIsImageManagerOpen(true)}
                      className="h-8 bg-white border-gray-200 hover:bg-gray-50 text-[#087a83]"
                    >
                      <ImagePlus className="h-4 w-4 mr-2" />
                      <span>Adicionar imagens</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Price and Currency Section - Moved to the end */}
            <div className="mt-4 pt-4 border-t">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-4">
                  <StyledLabel htmlFor={`price-${flight.id}`} className="w-32" icon={DollarSign}>Preço</StyledLabel>
                  <div className="flex items-center gap-2">
                    <FormInput 
                      id={`price-${flight.id}`}
                      type="number"
                      value={flight.price || ''} 
                      onChange={(e) => handleUpdate('price', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="0.00" 
                      className="max-w-[120px]"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <StyledLabel htmlFor={`currency-${flight.id}`} className="w-32" icon={Tag}>Moeda</StyledLabel>
                  <FormInput 
                    id={`currency-${flight.id}`}
                    value={flight.currency || ''} 
                    onChange={(e) => handleUpdate('currency', e.target.value)}
                    placeholder="BRL" 
                    className="max-w-[80px]"
                  />
                </div>
              </div>
            </div>

            {/* Notes Section - Moved to the end */}
            <div className="mt-4 pt-4 border-t">
              <StyledLabel htmlFor={`notes-${flight.id}`} className="mb-2 block" icon={FileText}>Notas</StyledLabel>
              <FormTextarea 
                id={`notes-${flight.id}`}
                value={flight.notes || ''} 
                onChange={(e) => handleUpdate('notes', e.target.value)}
                placeholder="Informações adicionais sobre o voo..." 
                className="min-h-[100px]"
              />
            </div>

            {/* Airport Locations Section */}
            <div className="mt-4 pt-4 border-t">
              <h4 className="text-sm font-medium mb-3">Localizações dos Aeroportos</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Departure Airport Location */}
                <div className="space-y-2">
                  <StyledLabel icon={MapPin}>Aeroporto de Partida</StyledLabel>
                  <PlacesAutocompleteV1
                    value={flight.departureLocationName || flight.departureAirport || ''}
                    onChange={(value) => handleUpdate('departureLocationName', value)}
                    onPlaceSelect={(place) => {
                      if (place) {
                        // Use batch update if available for atomic update
                        if (batchUpdateFlight) {
                          // Create an updates object with all departure location-related fields
                          const updates: Partial<FlightInfo> = {
                            departurePlaceId: place.placeId,
                            departureLocationName: place.name,
                            departureCoordinates: place.coordinates ? {
                              lat: Number(place.coordinates.lat),
                              lng: Number(place.coordinates.lng)
                            } : undefined,
                            departureGoogleMapsUrl: place.googleMapsUrl,
                            departureCity: place.city,
                            departureCountry: place.country
                          };
                          
                          // Update all fields in a single operation
                          batchUpdateFlight(flight.id, updates);
                          
                          console.log('[FlightForm] Batch updated departure location fields:', {
                            placeId: place.placeId,
                            name: place.name,
                            coordinates: updates.departureCoordinates
                          });
                        } 
                        // Fallback to individual updates if batchUpdate is not available
                        else {
                          handleUpdate('departurePlaceId', place.placeId);
                          handleUpdate('departureLocationName', place.name);
                          
                          if (place.coordinates) {
                            const normalizedCoords = {
                              lat: Number(place.coordinates.lat),
                              lng: Number(place.coordinates.lng)
                            };
                            handleUpdate('departureCoordinates', normalizedCoords);
                          }
                          
                          handleUpdate('departureGoogleMapsUrl', place.googleMapsUrl);
                          handleUpdate('departureCity', place.city);
                          handleUpdate('departureCountry', place.country);
                        }
                        
                        // Notify user about the location confirmation
                        toast({
                          title: "Local de partida confirmado",
                          description: `${place.name} foi vinculado como aeroporto de partida.`,
                        });
                      }
                    }}
                    placeholder={`Digite o nome do aeroporto de partida (${flight.departureAirport || 'ex: GRU'})`}
                  />
                  {flight.departurePlaceId && (
                    <div className="text-xs text-green-600 flex items-center mt-1">
                      <Check className="h-3 w-3 mr-1" />
                      <span>Local confirmado</span>
                    </div>
                  )}
                </div>

                {/* Arrival Airport Location */}
                <div className="space-y-2">
                  <StyledLabel icon={MapPin}>Aeroporto de Chegada</StyledLabel>
                  <PlacesAutocompleteV1
                    value={flight.arrivalLocationName || (() => {
                      // Get the arrival airport from the last connection if it exists, otherwise use the main flight's arrival airport
                      if (flight.connections && flight.connections.length > 0) {
                        const lastConnection = flight.connections[flight.connections.length - 1];
                        return lastConnection.arrivalAirport;
                      }
                      return flight.arrivalAirport;
                    })() || ''}
                    onChange={(value) => handleUpdate('arrivalLocationName', value)}
                    onPlaceSelect={(place) => {
                      if (place) {
                        // Use batch update if available for atomic update
                        if (batchUpdateFlight) {
                          // Create an updates object with all arrival location-related fields
                          const updates: Partial<FlightInfo> = {
                            arrivalPlaceId: place.placeId,
                            arrivalLocationName: place.name,
                            arrivalCoordinates: place.coordinates ? {
                              lat: Number(place.coordinates.lat),
                              lng: Number(place.coordinates.lng)
                            } : undefined,
                            arrivalGoogleMapsUrl: place.googleMapsUrl,
                            arrivalCity: place.city,
                            arrivalCountry: place.country
                          };
                          
                          // Update all fields in a single operation
                          batchUpdateFlight(flight.id, updates);
                          
                          console.log('[FlightForm] Batch updated arrival location fields:', {
                            placeId: place.placeId,
                            name: place.name,
                            coordinates: updates.arrivalCoordinates
                          });
                        } 
                        // Fallback to individual updates if batchUpdate is not available
                        else {
                          handleUpdate('arrivalPlaceId', place.placeId);
                          handleUpdate('arrivalLocationName', place.name);
                          
                          if (place.coordinates) {
                            const normalizedCoords = {
                              lat: Number(place.coordinates.lat),
                              lng: Number(place.coordinates.lng)
                            };
                            handleUpdate('arrivalCoordinates', normalizedCoords);
                          }
                          
                          handleUpdate('arrivalGoogleMapsUrl', place.googleMapsUrl);
                          handleUpdate('arrivalCity', place.city);
                          handleUpdate('arrivalCountry', place.country);
                        }
                        
                        // Notify user about the location confirmation
                        toast({
                          title: "Local de chegada confirmado",
                          description: `${place.name} foi vinculado como aeroporto de chegada.`,
                        });
                      }
                    }}
                    placeholder={`Digite o nome do aeroporto de chegada (${(() => {
                      // Get the arrival airport from the last connection if it exists, otherwise use the main flight's arrival airport
                      if (flight.connections && flight.connections.length > 0) {
                        const lastConnection = flight.connections[flight.connections.length - 1];
                        return lastConnection.arrivalAirport || 'ex: CDG';
                      }
                      return flight.arrivalAirport || 'ex: CDG';
                    })()})`}
                  />
                  {flight.arrivalPlaceId && (
                    <div className="text-xs text-green-600 flex items-center mt-1">
                      <Check className="h-3 w-3 mr-1" />
                      <span>Local confirmado</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Opção para mostrar como eventos separados */}
            <div className="flex items-center space-x-2 mt-6 border-t pt-4">
              <Checkbox 
                id={`separate-events-${flight.id}`}
                checked={flight.showAsSeparateEvents === true}
                onCheckedChange={(checked) => {
                  handleUpdate('showAsSeparateEvents', checked === true);
                }}
              />
              <Label 
                htmlFor={`separate-events-${flight.id}`}
                className="text-sm font-medium cursor-pointer select-none"
              >
                Mostrar partida e chegada como eventos separados no roteiro
              </Label>
            </div>
          </CardContent>
        )}
      </Card>
      
      {/* Modal para Salvar na Biblioteca */}
      {isSaveToLibraryOpen && (
        <SaveToLibraryModal
          isOpen={isSaveToLibraryOpen}
          onClose={() => setIsSaveToLibraryOpen(false)}
          item={flight}
          itemType="flight"
          onSave={(savedItem) => {
            setIsSavedToLibrary(true);
            toast({
              title: "Voo salvo na biblioteca",
              description: "O voo foi salvo com sucesso na sua biblioteca.",
            });
            setIsSaveToLibraryOpen(false);
          }}
        />
      )}
      
      {isImageManagerOpen && (
        <ImageManagerModal
          isOpen={isImageManagerOpen}
          onClose={() => setIsImageManagerOpen(false)}
          images={flight.images || []}
          onImagesChange={(images) => {
            handleUpdate('images', images);
          }}
          contextId={flight.id}
          contextType="flight"
        />
      )}
      
      {/* Modal para seleção de página */}
      {isPageSelectionOpen && (
        <PageSelectionModal
          isOpen={isPageSelectionOpen}
          onClose={() => {
            setIsPageSelectionOpen(false);
            setFlightToMove(null);
          }}
          pages={Array.isArray(allPages) ? allPages : []}
          onSelectPage={handleMoveToPage}
          currentPageId={pageId}
          title="Selecionar página para mover o voo"
        />
      )}
    </>
  );
} 